import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../Component/Layout';
import axios from 'axios';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PURCHASE_API } from "../../apiConfig";

// same select styling as Product Master (Product.js)
const customSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

const initialFilters = {
    productType: null,
    category: null,
    subCategory: null,
    qualityCategory: null,
    cableType: null,
    construction: null,
    code: '',
    color: '',
    cores: null,
    crossSection: null,
    voltage: null,
    referenceOfStandard: null,
    description: '',
    productCode: '',
    batchNumber: ''
};

function BatchTree() {

    const navigate = useNavigate();

    const [filters, setFilters] = useState(initialFilters);

    const [filterOptions, setFilterOptions] = useState({
        category: [], cableType: [], code: [], color: [], noOfCores: [],
        nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [],
        subCategory: [], qualityCategory: []
    });

    const [productTypes, setProductTypes] = useState([]);
    const [codeDescList, setCodeDescList] = useState([]);
    const [batchNumberList, setBatchNumberList] = useState([]);

    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // BOM tree modal (only tree available now — shows BOM breakdown)
    const [showBomTreeModal, setShowBomTreeModal] = useState(false);
    const [bomTreeLoading, setBomTreeLoading] = useState(false);
    const [bomTree, setBomTree] = useState(null);
    const [expandedBomNodes, setExpandedBomNodes] = useState({});

    // stock batch breakdown modal (opens on top of the BOM tree modal)
    const [showStockModal, setShowStockModal] = useState(false);
    const [stockLoading, setStockLoading] = useState(false);
    const [stockBatches, setStockBatches] = useState([]);
    const [stockProductCode, setStockProductCode] = useState(null);

    const toStrOptions = (arr) => (arr || []).map(v => ({ value: v, label: v }));
    const getOptions = (arr) => (arr || []).map(v => (v && typeof v === 'object' ? { value: v.name, label: v.name } : { value: v, label: v }));

    const selectedType = (filters.productType?.value || "").trim().toLowerCase();
    const isRawMaterial = selectedType === "raw material";
    const isSemiFinished = selectedType.includes("semi finished");
    const isFinishedLike = selectedType && !isRawMaterial && !isSemiFinished;

    const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
        if (k === 'productType') return false;
        return v !== null && v !== '';
    }).length;

    const updateFilter = (patch) => {
        setCurrentPage(1);
        setFilters(prev => ({ ...prev, ...patch }));
    };

    const clearAll = () => {
        setCurrentPage(1);
        setFilters(initialFilters);
    };

    // ================= PRODUCT TYPES =================
    useEffect(() => {
        const fetchProductTypes = async () => {
            try {
                const res = await axios.get(`${PURCHASE_API}/batch-tree/product-types`);
                setProductTypes(res.data || []);
            } catch (e) {
                console.error("Failed to fetch product types", e);
            }
        };
        fetchProductTypes();
    }, []);

    // ================= CASCADING FILTER OPTIONS (same as Product Master) =================
    const fetchFilterOptions = useCallback(async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/batch-tree/filter-options`, {
                params: {
                    productType: filters.productType?.value || "",
                    category: filters.category?.value || "",
                    cableType: filters.cableType?.value || "",
                    code: filters.code || "",
                    color: filters.color || "",
                    noOfCores: filters.cores?.value || "",
                    nominalCrossSection: filters.crossSection?.value || "",
                    referenceOfStandard: filters.referenceOfStandard?.value || "",
                    voltage: filters.voltage?.value || "",
                    subCategory: filters.subCategory?.value || ""
                }
            });
            setFilterOptions(prev => ({ ...prev, ...res.data }));
        } catch (e) {
            console.error("Failed to fetch filter options", e);
        }
    }, [
        filters.productType, filters.category, filters.cableType, filters.code,
        filters.color, filters.cores, filters.crossSection,
        filters.referenceOfStandard, filters.voltage, filters.subCategory
    ]);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // ================= PRODUCT CODE / DESCRIPTION LIST (linked dropdowns) =================
    useEffect(() => {
        const fetchCodeDescList = async () => {
            try {
                const res = await axios.get(`${PURCHASE_API}/batch-tree/product-codes`, {
                    params: {
                        productType: filters.productType?.value || "",
                        category: filters.category?.value || "",
                        subCategory: filters.subCategory?.value || "",
                        cableType: filters.cableType?.value || ""
                    }
                });
                setCodeDescList(res.data || []);
            } catch (e) {
                console.error("Failed to fetch product codes", e);
            }
        };
        fetchCodeDescList();
    }, [filters.productType, filters.category, filters.subCategory, filters.cableType]);

    // ================= BATCH NUMBER LIST (dropdown options, from stock_master) =================
    useEffect(() => {
        const fetchBatchNumberList = async () => {
            try {
                const res = await axios.get(`${PURCHASE_API}/batch-tree/batch-numbers`, {
                    params: {
                        productType: filters.productType?.value || "",
                        productCode: filters.productCode || ""
                    }
                });
                setBatchNumberList(res.data || []);
            } catch (e) {
                console.error("Failed to fetch batch numbers", e);
            }
        };
        fetchBatchNumberList();
    }, [filters.productType, filters.productCode]);

    // ================= MAIN GRID =================
    const fetchRows = useCallback(async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/batch-tree`, {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    productType: filters.productType?.value || "",
                    category: filters.category?.value || "",
                    subCategory: filters.subCategory?.value || "",
                    qualityCategory: filters.qualityCategory?.value || "",
                    cableType: filters.cableType?.value || "",
                    construction: filters.construction?.value || "",
                    code: filters.code || "",
                    color: filters.color || "",
                    noOfCores: filters.cores?.value || "",
                    nominalCrossSection: filters.crossSection?.value || "",
                    voltage: filters.voltage?.value || "",
                    referenceOfStandard: filters.referenceOfStandard?.value || "",
                    description: filters.description || "",
                    productCode: filters.productCode || "",
                    batchNumber: filters.batchNumber || ""
                }
            });
            setRows(res.data.data || []);
            setTotalRows(res.data.total || 0);
            setTotalPages(Math.ceil((res.data.total || 0) / itemsPerPage) || 1);
        } catch (e) {
            console.error("Failed to fetch batch tree data", e);
        }
    }, [filters, currentPage]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    // ================= BOM TREE (self-contained batch-tree endpoint, no BOM module dependency) =================
    // traces the real production genealogy for the selected product+batch
    const openBomTree = async (productCode, batchNumber) => {
        setShowBomTreeModal(true);
        setBomTree(null);
        setExpandedBomNodes({});
        setBomTreeLoading(true);
        try {
            const url = `${PURCHASE_API}/batch-tree/${encodeURIComponent(productCode)}/bom-tree` +
                (batchNumber ? `?batchNumber=${encodeURIComponent(batchNumber)}` : "");
            const res = await axios.get(url);
            setBomTree(res.data.data);
        } catch (e) {
            console.error("Failed to fetch BOM tree", e);
            toast.error("Failed to load BOM tree");
        } finally {
            setBomTreeLoading(false);
        }
    };

    // ================= STOCK BATCH BREAKDOWN (click on a node's Stock Qty) =================
    const openStockBatches = async (productCode, batchNumber, confirmationId) => {
        setStockProductCode(productCode);
        setShowStockModal(true);
        setStockBatches([]);
        setStockLoading(true);
        try {
            const params = new URLSearchParams();
            if (batchNumber) params.set("batchNumber", batchNumber);
            if (confirmationId) params.set("confirmationId", confirmationId);
            const qs = params.toString();
            const url = `${PURCHASE_API}/batch-tree/${encodeURIComponent(productCode)}/stock-batches` +
                (qs ? `?${qs}` : "");
            const res = await axios.get(url);
            setStockBatches(res.data.data || []);
        } catch (e) {
            console.error("Failed to fetch stock batches", e);
            toast.error("Failed to load stock batch details");
        } finally {
            setStockLoading(false);
        }
    };

    const toggleBomNode = (code) => {
        setExpandedBomNodes((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const renderBomNode = (node, level = 0) => {
        if (!node) return null;

        const hasChildren = node.children && node.children.length > 0;
        const nodeKey = `${node.code}-${node.batch || 'nb'}`;
        const expanded = expandedBomNodes[nodeKey] ?? true;

        return (
            <div key={nodeKey} style={{ marginLeft: level * 25, marginTop: 6 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "8px 12px",
                        background: level === 0 ? "#dbeafe" : "#ffffff",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        boxShadow: "0 2px 5px rgba(0,0,0,0.08)"
                    }}
                >
                    {hasChildren ? (
                        <button
                            onClick={() => toggleBomNode(nodeKey)}
                            style={{
                                width: 20,
                                height: 20,
                                fontSize: 12,
                                border: "none",
                                borderRadius: "50%",
                                background: "#2563eb",
                                color: "white",
                                cursor: "pointer"
                            }}
                        >
                            {expanded ? "-" : "+"}
                        </button>
                    ) : (
                        <span style={{ fontSize: 20 }}>&bull;</span>
                    )}

                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: "#1e293b"
                            }}
                        >
                            {node.code}
                            {node.batch && (
                                <span
                                    style={{ fontWeight: 500, fontSize: 11, color: "#2563eb", marginLeft: 8, cursor: "pointer", textDecoration: "underline" }}
                                    title="View batch-wise stock breakdown"
                                    onClick={() => openStockBatches(node.code, node.batch, node.consumed_confirmation_id)}
                                >
                                    (Batch: {node.batch})
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 10, color: "#666" }}>
                            {node.description || "No Description"}
                        </div>
                        {node.uom && (
                            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                                UOM: <strong style={{ color: "#334155" }}>{node.uom}</strong>
                            </div>
                        )}
                        <div style={{ fontSize: 10, marginTop: 2, width: "fit-content" }}>
                            Stock Qty: <strong
                                style={{
                                    color: Number(node.stock_qty) > 0 ? "#16a34a" : "#dc2626"
                                }}
                            >
                                {node.stock_qty !== undefined && node.stock_qty !== null ? node.stock_qty : "-"}
                            </strong>
                        </div>
                    </div>

                    {hasChildren && (
                        <span
                            style={{
                                background: "#e0f2fe",
                                padding: "2px 8px",
                                borderRadius: 20,
                                fontSize: 10
                            }}
                        >
                            {node.children.length} items
                        </span>
                    )}
                </div>

                {expanded &&
                    hasChildren &&
                    node.children.map((child) => renderBomNode(child, level + 1))}
            </div>
        );
    };

    const fieldBox = { minWidth: 105, flex: "1 1 105px" };

    // common columns shown on every table, mirroring Product Master's column logic
    const commonTail = ["UOM", "Currency", isRawMaterial ? "Moving Avg Price" : "Price Per KM", "Cost"];

    let columns;
    if (isRawMaterial) {
        columns = ["Product Code", "Batch Number", "Description", "Code", "Category", "Sub Category", "Quality Category", ...commonTail, ""];
    } else if (isSemiFinished) {
        columns = ["Product Code", "Batch Number", "Description", "Product Type", "Category", ...commonTail, ""];
    } else if (isFinishedLike) {
        columns = ["Product Code", "Batch Number", "Description", "Code", "Construction", "Product Type", "Cable Type", "Category", "Color", "Cores", "Cross Section", "Ref Std", "Voltage", ...commonTail, ""];
    } else {
        columns = ["Product Code", "Batch Number", "Description", "Code", "Product Type", "Category", ...commonTail, ""];
    }

    return (
        <Layout>
            <div style={{ padding: 16 }}>

                <div style={styles.pageTitleBar}>
                    <div>
                        <div style={styles.pageTitle}>Batch Tree</div>
                        <div style={styles.pageSubtitle}>Trace product derivation chains using Product Master data</div>
                    </div>
                </div>

                {/* ================= FILTER BAR (matches Product Master exactly) ================= */}
                <div style={styles.filterCard}>

                    {/* PRODUCT TYPE + CLEAR ALL */}
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                        <div style={{ minWidth: 220 }}>
                            <label style={styles.compactLabel}>Product Type</label>
                            <Select
                                styles={customSelectStyles}
                                options={getOptions(productTypes)}
                                value={filters.productType}
                                placeholder="Select Product Type"
                                onChange={v => {
                                    setCurrentPage(1);
                                    setFilters({
                                        ...initialFilters,
                                        productType: v
                                    });
                                }}
                                isClearable
                            />
                        </div>

                        {activeFilterCount > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: 12, height: 38, whiteSpace: "nowrap" }}
                                onClick={clearAll}
                            >
                                &times; Clear All ({activeFilterCount})
                            </button>
                        )}
                    </div>

                    {/* TYPE-SPECIFIC FILTERS - single row, same order as Product Master */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 15 }}>

                        <div style={fieldBox} title={filters.category?.label || ''}>
                            <label style={styles.compactLabel}>Category</label>
                            <Select
                                styles={customSelectStyles}
                                options={toStrOptions(filterOptions.category)}
                                value={filters.category}
                                placeholder="Category"
                                onChange={v => updateFilter({
                                    category: v, cableType: null, code: '', color: '', cores: null,
                                    crossSection: null, referenceOfStandard: null, voltage: null,
                                    construction: null, subCategory: null, qualityCategory: null
                                })}
                                isClearable
                            />
                        </div>

                        {(isFinishedLike || !selectedType) && (
                            <>
                                <div style={fieldBox} title={filters.cableType?.label || ''}>
                                    <label style={styles.compactLabel}>Cable Type</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.cableType)}
                                        value={filters.cableType}
                                        placeholder="Cable Type"
                                        onChange={v => updateFilter({
                                            cableType: v, code: '', color: '', cores: null,
                                            crossSection: null, referenceOfStandard: null,
                                            voltage: null, construction: null
                                        })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.code || ''}>
                                    <label style={styles.compactLabel}>Code</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.code)}
                                        value={filters.code ? { label: filters.code, value: filters.code } : null}
                                        placeholder="Code"
                                        onChange={v => updateFilter({
                                            code: v ? v.value : '', color: '', cores: null,
                                            crossSection: null, referenceOfStandard: null,
                                            voltage: null, construction: null
                                        })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.color || ''}>
                                    <label style={styles.compactLabel}>Color</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.color)}
                                        value={filters.color ? { label: filters.color, value: filters.color } : null}
                                        placeholder="Color"
                                        onChange={v => updateFilter({
                                            color: v ? v.value : '', cores: null, crossSection: null,
                                            referenceOfStandard: null, voltage: null, construction: null
                                        })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.cores?.label || ''}>
                                    <label style={styles.compactLabel}>Cores</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.noOfCores)}
                                        value={filters.cores}
                                        placeholder="Cores"
                                        onChange={v => updateFilter({
                                            cores: v, crossSection: null, referenceOfStandard: null,
                                            voltage: null, construction: null
                                        })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.crossSection?.label || ''}>
                                    <label style={styles.compactLabel}>Cross Section</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.nominalCrossSection)}
                                        value={filters.crossSection}
                                        placeholder="Cross Section"
                                        onChange={v => updateFilter({
                                            crossSection: v, referenceOfStandard: null,
                                            voltage: null, construction: null
                                        })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.referenceOfStandard?.label || ''}>
                                    <label style={styles.compactLabel}>Std. Ref</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.referenceOfStandard)}
                                        value={filters.referenceOfStandard}
                                        placeholder="Std. Ref"
                                        onChange={v => updateFilter({ referenceOfStandard: v, voltage: null, construction: null })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.voltage?.label || ''}>
                                    <label style={styles.compactLabel}>Voltage</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.voltage)}
                                        value={filters.voltage}
                                        placeholder="Voltage"
                                        onChange={v => updateFilter({ voltage: v, construction: null })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.construction?.label || ''}>
                                    <label style={styles.compactLabel}>Construction</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.construction)}
                                        value={filters.construction}
                                        placeholder="Construction"
                                        onChange={v => updateFilter({ construction: v })}
                                        isClearable
                                    />
                                </div>
                            </>
                        )}

                        {isRawMaterial && (
                            <>
                                <div style={fieldBox} title={filters.subCategory?.label || ''}>
                                    <label style={styles.compactLabel}>Sub Category</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.subCategory)}
                                        value={filters.subCategory}
                                        placeholder="Sub Category"
                                        onChange={v => updateFilter({ subCategory: v, qualityCategory: null })}
                                        isClearable
                                    />
                                </div>

                                <div style={fieldBox} title={filters.qualityCategory?.label || ''}>
                                    <label style={styles.compactLabel}>Quality Category</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={toStrOptions(filterOptions.qualityCategory)}
                                        value={filters.qualityCategory}
                                        placeholder="Quality Category"
                                        onChange={v => updateFilter({ qualityCategory: v })}
                                        isClearable
                                    />
                                </div>
                            </>
                        )}

                    </div>

                    {/* PRODUCT CODE / DESCRIPTION - linked dropdowns, shown by default */}
                    <div style={{ display: "flex", gap: 15, marginTop: 15 }}>
                        <div style={{ minWidth: 220 }}>
                            <label style={styles.compactLabel}>Product Code</label>
                            <Select
                                styles={customSelectStyles}
                                options={codeDescList.map(item => ({
                                    value: item.productCode,
                                    label: item.productCode
                                }))}
                                value={filters.productCode ? { label: filters.productCode, value: filters.productCode } : null}
                                placeholder="Product Code..."
                                onChange={v => {
                                    const match = codeDescList.find(item => item.productCode === (v ? v.value : ''));
                                    updateFilter({
                                        productCode: v ? v.value : '',
                                        description: match ? (match.description || '') : ''
                                    });
                                }}
                                isClearable
                            />
                        </div>

                        <div style={{ flex: 1 }}>
                            <label style={styles.compactLabel}>Product Description</label>
                            <Select
                                styles={customSelectStyles}
                                options={codeDescList
                                    .filter(item => item.description)
                                    .map(item => ({
                                        value: item.description,
                                        label: item.description
                                    }))}
                                value={filters.description ? { label: filters.description, value: filters.description } : null}
                                placeholder="Product Description..."
                                onChange={v => {
                                    const match = codeDescList.find(item => item.description === (v ? v.value : ''));
                                    updateFilter({
                                        description: v ? v.value : '',
                                        productCode: match ? (match.productCode || '') : ''
                                    });
                                }}
                                isClearable
                            />
                        </div>

                        <div style={{ minWidth: 220 }}>
                            <label style={styles.compactLabel}>Batch Number</label>
                            <Select
                                styles={customSelectStyles}
                                options={batchNumberList.map(item => ({
                                    value: item.batchNumber,
                                    label: item.batchNumber
                                }))}
                                value={filters.batchNumber ? { label: filters.batchNumber, value: filters.batchNumber } : null}
                                placeholder="Batch Number..."
                                onChange={v => updateFilter({ batchNumber: v ? v.value : '' })}
                                isClearable
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: 8, fontSize: 13, color: "#16a34a", fontWeight: 600 }}>
                        {totalRows} products match
                    </div>

                </div>

                {/* ================= TABLE ================= */}
                <div style={styles.tableContainer}>
                    <table className="table" style={styles.erpTable}>
                        <thead>
                            <tr>
                                {columns.map(col => (
                                    <th key={col} style={styles.erpTh}>{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rows.map((r, i) => (
                                    <tr key={`${r.productCode}-${r.batchNumber || 'nb'}-${i}`}>
                                        <td style={styles.erpTd}>{r.productCode || "-"}</td>
                                        <td style={styles.erpTd}>{r.batchNumber || "-"}</td>
                                        <td style={styles.erpTd}>{r.description || "-"}</td>

                                        {!isSemiFinished && (
                                            <td style={styles.erpTd}>{r.code || "-"}</td>
                                        )}

                                        {isRawMaterial && (
                                            <>
                                                <td style={styles.erpTd}>{r.category || "-"}</td>
                                                <td style={styles.erpTd}>{r.subCategory || "-"}</td>
                                                <td style={styles.erpTd}>{r.qualityCategory || "-"}</td>
                                            </>
                                        )}

                                        {isFinishedLike && (
                                            <>
                                                <td style={styles.erpTd}>{r.construction || "-"}</td>
                                                <td style={styles.erpTd}>{r.productType || "-"}</td>
                                                <td style={styles.erpTd}>{r.cableType || "-"}</td>
                                                <td style={styles.erpTd}>{r.category || "-"}</td>
                                                <td style={styles.erpTd}>{r.color || "-"}</td>
                                                <td style={styles.erpTd}>{r.noOfCores || "-"}</td>
                                                <td style={styles.erpTd}>{r.nominalCrossSection || "-"}</td>
                                                <td style={styles.erpTd}>{r.referenceOfStandard || "-"}</td>
                                                <td style={styles.erpTd}>{r.voltage || "-"}</td>
                                            </>
                                        )}

                                        {isSemiFinished && (
                                            <>
                                                <td style={styles.erpTd}>{r.productType || "-"}</td>
                                                <td style={styles.erpTd}>{r.category || "-"}</td>
                                            </>
                                        )}

                                        {!isRawMaterial && !isFinishedLike && !isSemiFinished && (
                                            <>
                                                <td style={styles.erpTd}>{r.productType || "-"}</td>
                                                <td style={styles.erpTd}>{r.category || "-"}</td>
                                            </>
                                        )}

                                        <td style={styles.erpTd}>{r.uom || "-"}</td>
                                        <td style={styles.erpTd}>{r.currency || "-"}</td>
                                        <td style={styles.erpTd}>{(isRawMaterial ? r.movingAveragePrice : r.pricePerKm) || "-"}</td>
                                        <td style={styles.erpTd}>{r.productCost || "-"}</td>

                                        <td style={styles.erpTd}>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => openBomTree(r.productCode, r.batchNumber)}
                                            >
                                                Tree
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center" style={styles.erpTd}>
                                        No products found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 4px' }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                            {totalRows} product{totalRows === 1 ? '' : 's'} found
                        </span>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            >
                                Prev
                            </button>
                            <span style={{ fontSize: 12 }}>Page {currentPage} of {totalPages}</span>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= BOM TREE MODAL ================= */}
                {showBomTreeModal && (
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.5)",
                            zIndex: 1200
                        }}
                    >
                        <div
                            style={{
                                width: "70vw",
                                height: "75vh",
                                padding: 14,
                                margin: "50px auto",
                                background: "#f8fafc",
                                borderRadius: 12,
                                overflowY: "auto"
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 16
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                                        Batch Tree
                                    </h3>
                                    <span
                                        style={{
                                            background: "#dbeafe",
                                            color: "#1d4ed8",
                                            padding: "4px 12px",
                                            borderRadius: 20,
                                            fontSize: 13,
                                            fontWeight: 700
                                        }}
                                    >
                                        Product: {bomTree?.code}
                                    </span>
                                </div>

                                <button
                                    onClick={() => setShowBomTreeModal(false)}
                                    style={{
                                        background: "#ef4444",
                                        color: "white",
                                        border: "none",
                                        padding: "6px 14px",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        fontSize: 12,
                                        fontWeight: 600
                                    }}
                                >
                                    Close
                                </button>
                            </div>

                            {bomTreeLoading ? (
                                <div style={{ fontSize: 12, color: '#64748b' }}>Loading...</div>
                            ) : bomTree ? (
                                renderBomNode(bomTree)
                            ) : (
                                <div style={{ fontSize: 12, color: '#64748b' }}>No BOM tree data.</div>
                            )}
                        </div>
                    </div>
                )}

                {/* ================= STOCK BATCH BREAKDOWN MODAL ================= */}
                {showStockModal && (
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.5)",
                            zIndex: 1300,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                        onClick={() => setShowStockModal(false)}
                    >
                        <div
                            style={{
                                width: "min(600px, 90vw)",
                                maxHeight: "75vh",
                                padding: 16,
                                background: "#fff",
                                borderRadius: 10,
                                overflowY: "auto"
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 14
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <span
                                        style={{
                                            background: "#dbeafe",
                                            color: "#1d4ed8",
                                            padding: "3px 10px",
                                            borderRadius: 20,
                                            fontSize: 12,
                                            fontWeight: 700
                                        }}
                                    >
                                        {stockProductCode}
                                    </span>
                                </div>
                                <button
                                    onClick={() => setShowStockModal(false)}
                                    style={{
                                        background: "#ef4444",
                                        color: "white",
                                        border: "none",
                                        padding: "5px 12px",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        fontSize: 12,
                                        fontWeight: 600
                                    }}
                                >
                                    Close
                                </button>
                            </div>

                            {stockLoading ? (
                                <div style={{ fontSize: 12, color: '#64748b' }}>Loading...</div>
                            ) : stockBatches.length > 0 ? (
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Batch Number</th>
                                            <th style={styles.erpTh}>Qty</th>
                                            <th style={styles.erpTh}>Storage Location</th>
                                            <th style={styles.erpTh}>Document No</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stockBatches.map((b, i) => {
                                            const handleDocumentClick = () => {
                                                if (!b.document_no) return;
                                                const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
                                                if (b.is_raw_material) {
                                                    // Raw Material -> the GRN that brought this batch into stock
                                                    navigate(`/cable/goods-receipt/goods-receipt?view=${encodeURIComponent(b.document_no)}&returnTo=${returnTo}`);
                                                } else if (b.production_order_number) {
                                                    // Semi Finished / Finished Goods -> the confirmation that produced this batch
                                                    navigate(`/cable/confirmation?productionOrderNumber=${encodeURIComponent(b.production_order_number)}&confirmationId=${encodeURIComponent(b.document_no)}&returnTo=${returnTo}`);
                                                }
                                            };

                                            return (
                                                <tr key={i}>
                                                    <td style={styles.erpTd}>{b.product_code || "-"}</td>
                                                    <td style={styles.erpTd}>{b.batch_number || "-"}</td>
                                                    <td style={styles.erpTd}>{b.qty ?? "-"}</td>
                                                    <td style={styles.erpTd}>{b.storage_location || "-"}</td>
                                                    <td style={styles.erpTd}>
                                                        {b.document_no ? (
                                                            <span
                                                                onClick={handleDocumentClick}
                                                                style={{ color: "#2563eb", textDecoration: "underline", cursor: "pointer" }}
                                                            >
                                                                {b.document_no}
                                                            </span>
                                                        ) : "-"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ fontSize: 12, color: '#64748b' }}>No stock batches found.</div>
                            )}
                        </div>
                    </div>
                )}

                <ToastContainer position="top-right" autoClose={3000} />
            </div>
        </Layout>
    );
}

const styles = {
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 15 },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px", borderBottom: "1px solid #e5e7eb", color: "#1e293b" },
    erpTd: { fontSize: 13, padding: "10px", borderBottom: "1px solid #f1f5f9", color: "#334155" }
};

export default BatchTree;